import React from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CropFreeOutlinedIcon from "@mui/icons-material/CropFreeOutlined";

const ACCENT = "#a16207";

const PLAID = `
  repeating-linear-gradient(90deg, #6b3a2a 0 3px, transparent 3px 11px),
  repeating-linear-gradient(#6b3a2a 0 3px, #e4c79a 3px 11px)
`;

const dropIn = keyframes`
  0% { transform: translateY(-16px) rotate(-8deg); opacity: 0; }
  22%, 78% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { opacity: 0; }
`;

const sliderPulse = keyframes`
  0%, 20% { width: 42%; }
  50%, 70% { width: 78%; }
  100% { width: 42%; }
`;

const gridIn = keyframes`
  0%, 38% { opacity: 0; transform: scale(0.92); }
  55%, 84% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; }
`;

const historyIn = keyframes`
  0%, 25% { opacity: 0; transform: translateY(8px); }
  45%, 80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

function Stage({ children }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 118,
        overflow: "hidden",
        bgcolor: "#f3efe8",
      }}
    >
      {children}
    </Box>
  );
}

function PhotoScene() {
  return (
    <Stage>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 22,
          width: 92,
          height: 72,
          ml: "-46px",
          backgroundImage: PLAID,
          borderRadius: 1,
          boxShadow: "0 8px 18px rgba(80,60,40,0.12)",
          animation: `${dropIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
    </Stage>
  );
}

function OptionsScene() {
  return (
    <Stage>
      <Box
        sx={{
          position: "absolute",
          left: 22,
          right: 22,
          top: 28,
          display: "grid",
          gap: 1.4,
        }}
      >
        {[0, 1].map((i) => (
          <Box key={i}>
            <Box
              sx={{
                height: 6,
                borderRadius: 99,
                bgcolor: alpha(ACCENT, 0.18),
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  borderRadius: 99,
                  bgcolor: ACCENT,
                  animation: `${sliderPulse} 4.8s ease-in-out infinite`,
                  animationDelay: `${i * 0.35}s`,
                  ...motionSafe,
                }}
              />
            </Box>
          </Box>
        ))}
        <Box
          sx={{
            mt: 0.4,
            height: 26,
            borderRadius: 1,
            border: "1px solid",
            borderColor: alpha(ACCENT, 0.35),
            bgcolor: "#fffdf8",
            display: "flex",
            alignItems: "center",
            px: 1,
            fontSize: 11,
            fontWeight: 700,
            color: ACCENT,
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 4,
              borderRadius: 99,
              bgcolor: alpha(ACCENT, 0.35),
            }}
          />
          ▾
        </Box>
      </Box>
    </Stage>
  );
}

function GenerateScene() {
  return (
    <Stage>
      <CropFreeOutlinedIcon
        sx={{
          position: "absolute",
          left: 22,
          top: 46,
          color: ACCENT,
          fontSize: 22,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 22,
          top: 16,
          width: 120,
          height: 86,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2px",
          animation: `${gridIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} sx={{ backgroundImage: PLAID }} />
        ))}
      </Box>
    </Stage>
  );
}

function HistoryScene() {
  return (
    <Stage>
      <Box
        sx={{
          position: "absolute",
          left: 18,
          top: 18,
          width: 78,
          height: 82,
          borderRadius: 1,
          backgroundImage: PLAID,
          border: "1px solid",
          borderColor: alpha(ACCENT, 0.3),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: 16,
          top: 20,
          width: 92,
          display: "grid",
          gap: 0.7,
          animation: `${historyIn} 4.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              height: 22,
              borderRadius: 1,
              bgcolor: "#fffdf8",
              border: "1px solid",
              borderColor: alpha(ACCENT, 0.22),
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              px: 0.6,
              opacity: 1 - i * 0.18,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: 0.4,
                backgroundImage: PLAID,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 99,
                bgcolor: alpha(ACCENT, 0.2),
              }}
            />
          </Box>
        ))}
      </Box>
    </Stage>
  );
}

function SceneCard({ preview, title, body }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: alpha(ACCENT, 0.22),
        bgcolor: "background.paper",
        boxShadow: `0 10px 28px ${alpha(ACCENT, 0.06)}`,
      }}
    >
      {preview}
      <Box sx={{ p: 1.5 }}>
        <Typography fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.55, lineHeight: 1.45 }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

export default function SkillAgentTileHelpDialog({ open, onClose }) {
  const { t } = useTranslation();
  const key = "skillAgent.usageHelp.tile";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={800}>
          {t(`${key}.title`)}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(`${key}.lead`)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1.25,
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <SceneCard
            preview={<PhotoScene />}
            title={t(`${key}.scenePhotoTitle`)}
            body={t(`${key}.scenePhotoBody`)}
          />
          <SceneCard
            preview={<OptionsScene />}
            title={t(`${key}.sceneOptionsTitle`)}
            body={t(`${key}.sceneOptionsBody`)}
          />
          <SceneCard
            preview={<GenerateScene />}
            title={t(`${key}.sceneGenerateTitle`)}
            body={t(`${key}.sceneGenerateBody`)}
          />
          <SceneCard
            preview={<HistoryScene />}
            title={t(`${key}.sceneHistoryTitle`)}
            body={t(`${key}.sceneHistoryBody`)}
          />
        </Box>
        <Box
          sx={{
            mt: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: alpha(ACCENT, 0.08),
            display: "grid",
            gap: 1.1,
          }}
        >
          {[
            ["noteSizeTitle", "noteSize"],
            ["noteWeaveTitle", "noteWeave"],
            ["noteHistoryTitle", "noteHistory"],
          ].map(([title, body]) => (
            <Box key={title}>
              <Typography fontWeight={800} sx={{ fontSize: "0.84rem" }}>
                {t(`${key}.${title}`)}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.3, lineHeight: 1.45 }}
              >
                {t(`${key}.${body}`)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1.25, lineHeight: 1.45 }}
        >
          {t(`${key}.againHint`)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
        >
          {t("skillAgent.usageHelp.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
